import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Mail, Search, ArrowRight, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Homepage nudge: lets a visitor type their email, learn whether they have
 * any past or in-flight bookings linked to it, then deep-link them straight
 * into Login (existing account) or Register (auto-links pending guest bookings).
 *
 * Privacy: backend returns only count + has_account flag — no booking detail.
 */
export default function TrackBookingsNudge() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const lookup = async (e) => {
    e?.preventDefault?.();
    const cleaned = email.trim().toLowerCase();
    if (!cleaned || !cleaned.includes("@")) {
      setError("Enter a valid email");
      return;
    }
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const res = await axios.post(`${API}/customer/lookup`, { email: cleaned });
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Lookup failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const goAuth = () => {
    const cleaned = email.trim().toLowerCase();
    const path = result?.has_account ? "/customer/login" : "/customer/register";
    navigate(`${path}?email=${encodeURIComponent(cleaned)}`);
  };

  return (
    <div className="mb-4 rounded-2xl border border-slate-200/70 bg-white/95 backdrop-blur p-4 shadow-sm" data-testid="track-bookings-nudge">
      {!result && (
        <>
          <div className="flex items-center gap-2 mb-2">
            <Search className="h-4 w-4 text-orange-500" />
            <p className="text-sm font-semibold text-slate-900">Track your existing booking</p>
          </div>
          <p className="text-xs text-slate-500 mb-3">Enter the email you booked with — we'll show you any previous orders linked to it.</p>
          <form onSubmit={lookup} className="flex gap-2">
            <div className="relative flex-1">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-slate-200 focus:border-orange-400 focus:ring-1 focus:ring-orange-400 outline-none"
                data-testid="track-bookings-email"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-semibold text-white bg-slate-900 hover:bg-slate-700 rounded-lg disabled:opacity-60 active:scale-95 transition"
              data-testid="track-bookings-submit"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Check"}
            </button>
          </form>
          {error && <p className="mt-2 text-xs text-red-600" data-testid="track-bookings-error">{error}</p>}
        </>
      )}

      {result && result.booking_count === 0 && (
        <div className="text-center py-1">
          <p className="text-sm text-slate-700 font-medium">No bookings found for <span className="font-mono">{result.email}</span></p>
          <p className="text-xs text-slate-500 mt-1">Get an instant quote below to make your first booking.</p>
          <button onClick={() => { setResult(null); setEmail(""); }} className="mt-2 text-xs text-orange-600 font-semibold">Try a different email →</button>
        </div>
      )}

      {result && result.booking_count > 0 && (
        <div data-testid="track-bookings-result">
          <p className="text-sm text-slate-900 font-semibold">
            We found <span className="text-orange-600">{result.booking_count}</span> booking{result.booking_count === 1 ? "" : "s"} for <span className="font-mono">{result.email}</span>
          </p>
          <p className="text-xs text-slate-500 mt-1">
            {result.has_account
              ? "Log in to track delivery, view receipts, and message your driver."
              : "Create a free account — we'll link all your past bookings automatically."}
          </p>
          <div className="flex gap-2 mt-3">
            <button
              onClick={goAuth}
              className="flex-1 px-4 py-2 text-sm font-semibold text-white bg-orange-500 hover:bg-orange-600 rounded-lg active:scale-95 transition flex items-center justify-center gap-1"
              data-testid="track-bookings-cta"
            >
              {result.has_account ? "Log in" : "Create account"} <ArrowRight className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={() => { setResult(null); setEmail(""); }}
              className="px-3 py-2 text-xs text-slate-500 hover:text-slate-700"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
